import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

// Fix Leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Predefined location coordinates — operational cities (Feb 2026)
const CITY_COORDS = {
  'Bangalore': [12.9716, 77.5946],
  'Chennai':   [13.0827, 80.2707],
  'Hyderabad': [17.3850, 78.4867],
};

// Default coordinate (Bangalore) - for legacy bookings
const DEFAULT_LOC = [12.9716, 77.5946];

// Custom icon factory
const createIcon = (color, label) => {
  return L.divIcon({
    html: `<div style="
      background: ${color};
      border: 2px solid #000;
      width: 36px;
      height: 36px;
      border-radius: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 900;
      font-family: 'IBM Plex Mono', monospace;
      font-size: 14px;
      color: #000;
      box-shadow: 3px 3px 0px #0A0A0A;
    ">${label}</div>`,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  });
};

const pickupIcon = createIcon('#FFB800', 'P');
const destIcon = createIcon('#00C853', 'D');
const currentIcon = createIcon('#FF4500', 'L');

// Interpolation fallback — used only when no live GPS ping is available.
const STATUS_PROGRESS = {
  'BOOKED': 0,
  'PAID': 0,
  'SCHEDULED': 0,
  'PICKED_UP': 0.1,
  'IN_TRANSIT': 0.5,
  'ARRIVED_DESTINATION': 0.9,
  'OUT_FOR_DELIVERY': 0.85,
  'DELIVERED': 1.0,
  'DELAYED': 0.5,
  'DELAYED_DELIVERY': 0.85,
};

const interpolate = (start, end, t) => {
  return [
    start[0] + (end[0] - start[0]) * t,
    start[1] + (end[1] - start[1]) * t
  ];
};

// Map auto-fit component
const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions && positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, positions]);
  return null;
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const LIVE_POLL_MS = 15000;   // poll every 15s while a live ping exists

const TrackingMap = ({
  bookingId,
  pickupLocation,
  deliveryLocation,
  trackingStatus,
  pickupAddress,
  deliveryAddress,
}) => {
  const pickupCoords = CITY_COORDS[pickupLocation] || DEFAULT_LOC;
  const destCoords = CITY_COORDS[deliveryLocation] || DEFAULT_LOC;

  // Live GPS ping (populated by polling /live-location if bookingId is given)
  const [livePing, setLivePing] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;

    const fetchLive = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/bookings/${bookingId}/live-location`);
        if (cancelled) return;
        if (data && data.live && typeof data.lat === 'number' && typeof data.lng === 'number') {
          setLivePing(data);
        } else {
          setLivePing(null);
        }
      } catch (err) {
        if (!cancelled) setLivePing(null);
      }
    };

    fetchLive();
    timerRef.current = setInterval(fetchLive, LIVE_POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [bookingId, trackingStatus]);

  // Choose live GPS if available, else interpolate along the pickup→delivery line
  const progress = STATUS_PROGRESS[trackingStatus] ?? 0;
  const interpolatedPos = interpolate(pickupCoords, destCoords, progress);
  const currentPos = livePing ? [livePing.lat, livePing.lng] : interpolatedPos;

  const showCurrent = ['PICKED_UP', 'IN_TRANSIT', 'ARRIVED_DESTINATION',
                       'OUT_FOR_DELIVERY', 'DELAYED_DELIVERY', 'DELAYED'].includes(trackingStatus);
  const isDelivered = trackingStatus === 'DELIVERED';

  // Calculate center between pickup and destination
  const centerCoords = [
    (pickupCoords[0] + destCoords[0]) / 2,
    (pickupCoords[1] + destCoords[1]) / 2
  ];

  const liveLabel = livePing ? 'LIVE GPS' : 'ESTIMATED';
  const liveColor = livePing ? '#00C853' : '#FF4500';

  return (
    <div className="card-brutalist p-0 overflow-hidden" data-testid="tracking-map">
      <div className="px-6 py-4 border-b-2 border-black bg-white flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-bold uppercase" style={{ fontFamily: 'IBM Plex Mono' }}>
          LIVE LOCATION
        </h2>
        {showCurrent && (
          <span
            data-testid="tracking-live-badge"
            className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 border-2 border-black"
            style={{ backgroundColor: liveColor, color: '#000' }}
          >
            {livePing ? '● ' : ''}{liveLabel}
          </span>
        )}
      </div>
      <div style={{ height: '400px', width: '100%', position: 'relative' }}>
        <MapContainer
          center={centerCoords}
          zoom={6}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Pickup Marker */}
          <Marker position={pickupCoords} icon={pickupIcon}>
            <Popup>
              <strong>PICKUP — {pickupLocation || 'Origin'}</strong><br />
              {pickupAddress || 'Customer Address'}
            </Popup>
          </Marker>

          {/* Destination Marker */}
          <Marker position={destCoords} icon={destIcon}>
            <Popup>
              <strong>DELIVERY — {deliveryLocation}</strong><br />
              {deliveryAddress || deliveryLocation}
            </Popup>
          </Marker>

          {/* Route line */}
          <Polyline
            positions={[pickupCoords, destCoords]}
            color="#000000"
            weight={3}
            dashArray="8, 8"
            opacity={0.4}
          />

          {/* Traveled portion */}
          {showCurrent && (
            <Polyline
              positions={[pickupCoords, currentPos]}
              color="#FF4500"
              weight={4}
              opacity={0.9}
            />
          )}

          {/* Current location marker */}
          {showCurrent && (
            <Marker position={currentPos} icon={currentIcon}>
              <Popup>
                <strong>{livePing ? 'AGENT LIVE GPS' : 'ESTIMATED LOCATION'}</strong><br />
                Status: {trackingStatus.replace(/_/g, ' ')}<br />
                {livePing && (
                  <>Updated: {new Date(livePing.recorded_at).toLocaleTimeString()}<br /></>
                )}
                {livePing && livePing.agent_code && (
                  <>Agent: {livePing.agent_code}</>
                )}
              </Popup>
            </Marker>
          )}

          {/* If delivered, show full path */}
          {isDelivered && (
            <Polyline
              positions={[pickupCoords, destCoords]}
              color="#00C853"
              weight={4}
              opacity={0.9}
            />
          )}

          <FitBounds positions={[pickupCoords, destCoords]} />
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="px-6 py-3 border-t-2 border-black bg-white flex flex-wrap gap-4 items-center text-xs font-mono">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-black" style={{ backgroundColor: '#FFB800' }}></div>
          <span className="uppercase">Pickup</span>
        </div>
        {showCurrent && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-black" style={{ backgroundColor: '#FF4500' }}></div>
            <span className="uppercase">{livePing ? 'Live' : 'Estimated'}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-black" style={{ backgroundColor: '#00C853' }}></div>
          <span className="uppercase">Destination</span>
        </div>
        {!livePing && showCurrent && (
          <span className="text-[10px] text-neutral-500 ml-auto">
            Live GPS unavailable — showing estimated route
          </span>
        )}
      </div>
    </div>
  );
};

export default TrackingMap;
